import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { Grid, IconButton } from "@mui/material";
import { t } from "i18next";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import { SELECT_FIELD_TYPE, TEXT_FIELD_TYPE, AUTOCOMPLETE_FIELD_TYPE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { useGetReasons } from "../../services/customersService";
import { useLeadsStatuses } from "../../services/leadsService";
import { ContactsDialog } from "../PersonRepositoryPage/ContactsDialog";
import { useGetAllClauses } from "../../services/clausesService";

export const CUSTOMERS_TABLE_NAME = "customers_table";

export const useCustomersTableFieldsConfig = () => {
    const { data: statuses } = useLeadsStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`lead.status.${status.status_name_eng}`),
            })),
    });
    const { data: reasons } = useGetReasons({
        select: (data) =>
            data.results.map((reason) => ({
                value: reason.reason_name_eng,
                label: t(`clients.statusReasons.${reason.reason_name_eng}`),
            })),
    });

    const { data: allClauses,} = useGetAllClauses(
        new URLSearchParams({clause_active: true}),
        {
        select: (data) =>
            data.results.map((clause) => ({
                id: clause.id,
                name: clause.clause_name
            })),
        }
    );

    const { openDialog: openContactsDialog } = useDialogBetter();

    return [
        {
            name: "passportBtn",
            getValueCallback: (rowData) => {
                const clientType = rowData.company ? "company" : "customer";
                return (
                    <ClientPassportButton
                        clientId={rowData.id}
                        clientType={clientType}
                        color="primary"
                    />
                );
            },
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
        {
            name: "customer_number",
            getValueCallback: (rowData) => (
                <Grid
                    item
                    sx={{
                        minHeight: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {rowData.customer_number}
                </Grid>
            ),
            label: t("clients.table.customerNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "fname",
            getValueCallback: (rowData) => rowData.person?.name || "–",
            label: t("clients.table.firstName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "mname",
            getValueCallback: (rowData) => rowData.person?.name2 || "–",
            label: t("clients.table.middleName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "lname",
            getValueCallback: (rowData) => rowData.person?.surname || "–",
            label: t("clients.table.lastName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "company_full_name",
            getValueCallback: (rowData) => rowData.company?.name || "–",
            label: t("clients.table.companyName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
            minWidth: 120,
            width: 120,
        },
        {
            name: "company_short_name",
            getValueCallback: (rowData) => rowData.company?.name_short || "–",
            label: t("clients.table.companyShortName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
        },
        {
            name: "email",
            getValueCallback: (rowData) => {
                if (rowData.person) {
                    return rowData.person.email?.email_address || "–";
                }
                if (rowData.company) {
                    return rowData.company.email?.email_address || "–";
                }
                return "";
            },
            label: t("clients.table.email"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "phone",
            getValueCallback: (rowData) => {
                if (rowData.person) {
                    return rowData.person.phone?.phone_number || "–";
                }
                if (rowData.company) {
                    return rowData.company.phone?.phone_number || "–";
                }
                return "";
            },
            label: t("clients.table.phoneNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "pesel",
            getValueCallback: (rowData) => rowData.person?.pesel || "–",
            label: t("clients.table.pesel"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "nip",
            getValueCallback: (rowData) => rowData.company?.nip || "–",
            label: t("clients.table.nip"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "regon",
            getValueCallback: (rowData) => rowData.company?.regon || "–",
            label: t("clients.table.regon"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "legal",
            getValueCallback: (rowData) => rowData.company?.legal || "–",
            label: t("clients.table.legalForm"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => ({
                options: [
                    { value: "Sp. z.o.o.", label: "Sp. z o.o." },
                    { value: "SA", label: "SA" },
                    { value: "Sp. k.", label: "Sp. k." },
                    {
                        value: "Other",
                        label: t("clients.companyLegalTypes.Other"),
                    },
                ],
                optionKey: "value",
                optionLabel: "label",
            }),
            sortable: false,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: [
                    { value: "Sp. z.o.o.", label: "Sp. z o.o." },
                    { value: "SA", label: "SA" },
                    { value: "Sp. k.", label: "Sp. k." },
                    {
                        value: "Other",
                        label: t("clients.companyLegalTypes.Other"),
                    },
                ],
            },
        },
        // Business requirements to hide customer status
        // {
        //     name: "status",
        //     getValueCallback: (rowData) => t(`lead.status.${rowData.status}`),
        //     label: t("clients.table.status"),
        //     filterType: SELECT_FIELD_TYPE,
        //     sortable: true,
        //     getFilterFieldConfigCallback: (_) => ({
        //         options: statuses,
        //         optionKey: "value",
        //         optionLabel: "label",
        //     }),
        //     fieldConfig: {
        //         optionKey: "value",
        //         optionLabel: "label",
        //         options: statuses,
        //     },
        // },
        {
            name: "reason",
            getValueCallback: (rowData) => rowData.reason?.reason_name || "–",
            label: t("clients.table.statusReason"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => ({
                options: reasons,
                optionKey: "value",
                optionLabel: "label",
            }),
            sortable: true,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: reasons,
            },
        },
        {
            name: "city",
            getValueCallback: (rowData) => {
                if (rowData.person?.living_address) {
                    return rowData.person.living_address?.city || "–";
                }
                if (rowData.person?.mailing_address) {
                    return rowData.person.mailing_address?.city || "–";
                }
                return "";
            },
            label: t("clients.table.city"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "zip_code",
            getValueCallback: (rowData) => {
                if (rowData.person?.living_address) {
                    return rowData.person.living_address.zip_code || "–";
                }
                if (rowData.person?.mailing_address) {
                    return rowData.person.mailing_address.zip_code || "–";
                }
                return "";
            },
            label: t("clients.table.postalCode"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "street",
            getValueCallback: (rowData) => {
                if (rowData.person?.living_address) {
                    return rowData.person.living_address.street || "–";
                }
                if (rowData.person?.mailing_address) {
                    return rowData.person.mailing_address.street || "–";
                }
                return "";
            },
            label: t("clients.table.street"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "building",
            getValueCallback: (rowData) => {
                if (rowData.person?.living_address) {
                    return rowData.person.living_address.building || "–";
                }
                if (rowData.person?.mailing_address) {
                    return rowData.person.mailing_address.building || "–";
                }
                return "–";
            },
            label: t("clients.table.buildingNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "apartment",
            getValueCallback: (rowData) => {
                if (rowData.person?.living_address) {
                    return rowData.person.living_address.flat_nr || "–";
                }
                if (rowData.person?.mailing_address) {
                    return rowData.person.mailing_address.flat_nr || "–";
                }
                return "–";
            },
            label: t("clients.table.apartmentNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
        },
        {
            name: "birth_date",
            getValueCallback: (rowData) => rowData.birth_date || "–",
            label: t("clients.table.birthDate"),
        },
        {
            name: "segment",
            getValueCallback: (rowData) => rowData.segment,
            label: t("clients.table.customerSegment"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: true,
        },
        {
            name: "accepted_clauses",
            getValueCallback: (rowData) => rowData.accepted_clauses,
            label: t("clients.table.acceptedClauses"),
            filterType: AUTOCOMPLETE_FIELD_TYPE,
                fieldConfig: {
                    options: allClauses || [],
                    isObjectOption: true,
                    disableClearable: true,
                    multiple: true
                },
        },
        {
            name: "contact",
            getValueCallback: (rowData) => {
                return (
                    rowData.person && (
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openContactsDialog({
                                    Component: ContactsDialog,
                                    componentProps: {
                                        personId: rowData.person?.id || "–",
                                    },
                                });
                            }}
                        >
                            <LocalPhoneIcon />
                        </IconButton>
                    )
                );
            },
            label: t("clients.table.contact"),
            // @ts-ignore
            getFilterFieldConfigCallback: (_) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
